import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { apiClient } from '@/lib/api/client'
import type { HelmUser } from '@/lib/api/types'
import { useAsyncData } from '@/src/hooks/useAsyncData'

function userDisplayName(user: HelmUser) {
  return user.display_name || user.name || user.email
}

function userRoles(user: HelmUser) {
  return user.helm_roles.map((role) => role.role)
}

function isInternalUser(user: HelmUser) {
  const roles = userRoles(user)
  return roles.length === 0 || roles.some((role) => role !== 'customer')
}

function normalizeEmail(value: string | null | undefined) {
  return value?.trim().toLowerCase() || ''
}

export function HelmCcSelector({
  value,
  onChange,
  disabled,
  excludeEmails = [],
  label = 'CC Helm staff',
}: {
  value: string[]
  onChange: (value: string[]) => void
  disabled?: boolean
  excludeEmails?: Array<string | null | undefined>
  label?: string
}) {
  const excluded = new Set(excludeEmails.map(normalizeEmail).filter(Boolean))
  const { data, loading, error } = useAsyncData(async () => apiClient.listHelmUsers(), [])
  const users = (data?.users ?? [])
    .filter((user) => isInternalUser(user))
    .filter((user) => !excluded.has(normalizeEmail(user.email)))
    .sort((first, second) => userDisplayName(first).localeCompare(userDisplayName(second)))

  function toggle(email: string, checked: boolean) {
    if (checked) {
      onChange(Array.from(new Set([...value, email])))
      return
    }
    onChange(value.filter((item) => normalizeEmail(item) !== normalizeEmail(email)))
  }

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="rounded-md border p-3">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading Helm users...</p>
        ) : error ? (
          <p className="text-sm text-muted-foreground">Unable to load Helm users for CC selection.</p>
        ) : users.length === 0 ? (
          <p className="text-sm text-muted-foreground">No internal Helm users are available to copy.</p>
        ) : (
          <div className="grid gap-2 md:grid-cols-2">
            {users.map((user) => {
              const checked = value.some((email) => normalizeEmail(email) === normalizeEmail(user.email))
              const roles = userRoles(user).join(', ') || 'staff'
              return (
                <label key={user.id} className="flex cursor-pointer items-start gap-3 rounded-md p-2 hover:bg-muted/60">
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(nextChecked) => toggle(user.email, nextChecked === true)}
                    disabled={disabled}
                    aria-label={`CC ${userDisplayName(user)}`}
                  />
                  <span className="min-w-0 text-sm">
                    <span className="block truncate font-medium">{userDisplayName(user)}</span>
                    <span className="block truncate text-xs text-muted-foreground">{user.email} · {roles}</span>
                  </span>
                </label>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
